import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Shield, Gift, Check, Banknote, QrCode, Phone, Mail, Globe } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

interface BankAccount {
    bank: string;
    name: string;
    type?: string;
    number: string;
    branch: string;
    ifsc: string;
    swift?: string;
}

interface BankAccountSection {
    title: string;
    accounts: BankAccount[];
}

const Donate: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'online' | 'bank'>('online');
    const [amount, setAmount] = useState<number | null>(1000);
    const [isSuccess, setIsSuccess] = useState(false);

    const stats = [
        { value: '10,000+', label: 'Lives Impacted' },
        { value: '15+', label: 'Years of Service' },
        { value: '100%', label: 'Transparent' }
    ];

    const bankAccounts: BankAccountSection[] = [
        {
            title: 'Indian Currency Donations',
            accounts: [
                {
                    bank: 'Bank of Maharashtra',
                    name: '',
                    type: '',
                    number: '',
                    branch: '',
                    ifsc: ''
                },
                {
                    bank: 'Kotak Mahindra Bank',
                    name: '',
                    number: '',
                    branch: '',
                    ifsc: ''
                }
            ]
        },
        {
            title: 'Foreign Currency Donations (FCRA)',
            accounts: [
                {
                    bank: '',
                    name: '',
                    number: '',
                    branch: '',
                    ifsc: '',
                    swift: ''
                }
            ]
        }
    ];

    const registrationDetails = {
        fcra: '',
        csr: '',
        pan: ''
    };

    const contactInfo = {
        phone: '',
        email: '',
        website: ''
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSuccess(true);
    };

    if (isSuccess) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Header />
                <div className="max-w-4xl mx-auto px-4 py-20 text-center">
                    <div className="bg-white p-8 rounded-2xl shadow-lg">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Check className="w-10 h-10 text-green-600" />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-4">Thank You for Your Generosity!</h1>
                        <p className="text-gray-600 mb-6">
                            Your support helps us continue our mission to create positive change in communities.
                        </p>
                        <div className="space-y-4">
                            <p className="text-gray-700">
                                A confirmation has been sent to your email. For any queries, please contact us.
                            </p>
                            <button 
                                onClick={() => window.location.href = '/contact'}
                                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Contact Us
                            </button>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-900 to-purple-900 text-white py-20 px-4">
                <div className="max-w-6xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-6">Make a Difference Today</h1>
                    <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                        Your donation helps us create lasting change in communities. Every contribution, no matter how small, makes a real impact.
                    </p>
                </div>
            </div>

            {/* Stats */}
            <div className="max-w-6xl mx-auto px-4 -mt-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {stats.map((stat, index) => (
                        <motion.div 
                            key={index}
                            className="bg-white p-6 rounded-xl shadow-lg text-center"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <div className="text-3xl font-bold text-blue-600 mb-2">{stat.value}</div>
                            <div className="text-gray-600">{stat.label}</div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-6xl mx-auto px-4 py-12">
                {/* Tabs */}
                <div className="flex justify-center mb-8">
                    <div className="inline-flex bg-gray-100 p-1 rounded-lg">
                        <button
                            onClick={() => setActiveTab('online')}
                            className={`px-6 py-2 rounded-md font-medium ${activeTab === 'online' ? 'bg-white shadow' : 'text-gray-600'}`}
                        >
                            Online Donation
                        </button>
                        <button
                            onClick={() => setActiveTab('bank')}
                            className={`px-6 py-2 rounded-md font-medium ${activeTab === 'bank' ? 'bg-white shadow' : 'text-gray-600'}`}
                        >
                            Bank Transfer
                        </button>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {/* Left Column - Donation Form */}
                    <div className="md:col-span-2">
                        {activeTab === 'online' ? (
                            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                                <div className="p-6 md:p-8">
                                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Make a Donation</h2>
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Donation Amount (₹)</label>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                                                {[500, 1000, 2000, 5000, 10000].map((amt) => (
                                                    <button
                                                        key={amt}
                                                        type="button"
                                                        onClick={() => setAmount(amt)}
                                                        className={`p-3 border-2 rounded-lg font-medium transition-colors ${
                                                            amount === amt 
                                                                ? 'border-blue-600 bg-blue-50 text-blue-700' 
                                                                : 'border-gray-200 hover:border-blue-400'
                                                        }`}
                                                    >
                                                        ₹{amt.toLocaleString()}
                                                    </button>
                                                ))}
                                                <div className="relative">
                                                    <input
                                                        type="number"
                                                        placeholder="Other"
                                                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                                                        value={amount && ![500, 1000, 2000, 5000, 10000].includes(amount) ? amount : ''}
                                                        onChange={(e) => setAmount(Number(e.target.value))}
                                                    />
                                                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">₹</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                                <input
                                                    type="text"
                                                    required
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                                    <input
                                                        type="email"
                                                        required
                                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                                    <input
                                                        type="tel"
                                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:opacity-90 transition-opacity mt-4"
                                        >
                                            Donate Now
                                        </button>
                                    </form>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {bankAccounts.map((section, idx) => (
                                    <div key={idx} className="bg-white rounded-xl shadow-lg overflow-hidden">
                                        <div className="bg-gray-50 px-6 py-4 border-b">
                                            <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                                        </div>
                                        <div className="p-6 space-y-6">
                                            {section.accounts.map((account, accIdx) => (
                                                <div key={accIdx} className="space-y-3">
                                                    <div className="flex items-center">
                                                        <Banknote className="w-5 h-5 text-blue-600 mr-2" />
                                                        <h4 className="font-medium">{account.bank}</h4>
                                                    </div>
                                                    <div className="pl-7 space-y-2 text-sm text-gray-700">
                                                        <p><span className="font-medium">Account Name:</span> {account.name}</p>
                                                        {account.type && <p><span className="font-medium">Account Type:</span> {account.type}</p>}
                                                        <p><span className="font-medium">Account Number:</span> {account.number}</p>
                                                        <p><span className="font-medium">Branch:</span> {account.branch}</p>
                                                        <p><span className="font-medium">IFSC:</span> {account.ifsc}</p>
                                                        {account.swift && <p><span className="font-medium">SWIFT:</span> {account.swift}</p>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Registration Details</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                                            <div>
                                                <p><span className="font-medium">FCRA Registration:</span> {registrationDetails.fcra}</p>
                                                <p><span className="font-medium">12A, 80G, CSR Registration:</span> {registrationDetails.csr}</p>
                                                <p><span className="font-medium">PAN:</span> {registrationDetails.pan}</p>
                                            </div>
                                            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
                                                <p className="text-yellow-700">All donations are eligible for tax benefits as per applicable laws.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Info */}
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-xl shadow-lg">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">UPI Payment</h3>
                            <div className="space-y-4">
                                <div className="bg-blue-50 p-4 rounded-lg text-center">
                                    <QrCode className="w-12 h-12 mx-auto mb-3 text-blue-600" />
                                    <p className="text-sm text-gray-600 mb-2">Scan to pay using any UPI app</p>
                                    <div className="bg-white p-2 rounded border border-gray-200 mb-3">
                                        <div className="text-xs text-gray-500 mb-1">UPI ID</div>
                                        <div className="font-mono text-sm bg-gray-50 p-2 rounded">amzn0030095411@apl</div>
                                    </div>
                                    <p className="text-sm text-gray-600">Your small help can change someone's life!</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-lg">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
                            <p className="text-gray-600 mb-4">Contact us for any assistance with your donation or to discuss specific programs you'd like to support.</p>
                            <div className="space-y-3">
                                <div className="flex items-center">
                                    <Phone className="w-5 h-5 text-blue-600 mr-3" />
                                    <a href={`tel:${contactInfo.phone}`} className="text-gray-700 hover:text-blue-600">
                                        {contactInfo.phone}
                                    </a>
                                </div>
                                <div className="flex items-center">
                                    <Mail className="w-5 h-5 text-blue-600 mr-3" />
                                    <a href={`mailto:${contactInfo.email}`} className="text-gray-700 hover:text-blue-600">
                                        {contactInfo.email}
                                    </a>
                                </div>
                                <div className="flex items-center">
                                    <Globe className="w-5 h-5 text-blue-600 mr-3" />
                                    <a href={`https://${contactInfo.website}`} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-blue-600">
                                        {contactInfo.website}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-100">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Why Donate?</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-700">100% of your donation goes directly to our programs</span>
                                </li>
                                <li className="flex items-start">
                                    <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-700">Tax benefits under 80G of Income Tax Act</span>
                                </li>
                                <li className="flex items-start">
                                    <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-700">Transparent financial reporting</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            
            <Footer />
        </div>
    );
};

export default Donate;